import React, { useState, ChangeEvent, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CaretRight } from "../../../icons";
import Button from "../../common/button";
import {
  FireCalculatorResponse,
  useLazyGetToolDetailsQuery,
  useUpdateToolMutation,
} from "../../../store/api/toolsApi";
import { ErrorMessage } from "./error-message";
import { FireResults } from "./results";
import { useDebounce } from "../../../hooks/debounce-hooks";
import { toastError, toastSuccess } from "../../toast";
import { getLocalStorage } from "../../../helpers/storage";
import { MixpanelEvent, trackEvent } from "../../../analytics/mixpanel";

interface SliderProps {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step: number;
  label: string;
  description?: string;
  suffix?: string;
  error?: string;
}

function Slider({
  value,
  onChange,
  min,
  max,
  step,
  label,
  description,
  suffix = "Years",
  error,
}: SliderProps) {
  return (
    <div className="mb-8">
      <div className="flex justify-between items-baseline mb-2">
        <label className="text-white text-base">{label}</label>
        <span className="font-semibold text-white text-base">
          {value} {suffix}
        </span>
      </div>
      {description && (
        <p className="text-gray-400 text-sm mb-2">{description}</p>
      )}
      <div className="relative">
        <input
          type="range"
          min={min}
          max={max}
          value={value}
          step={step}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
          style={{
            background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${((value - min) / (max - min)) * 100}%, #374151 ${((value - min) / (max - min)) * 100}%, #374151 100%)`,
          }}
        />
        <div
          className="absolute -top-4 -translate-x-1/2 text-white text-sm px-2 py-0.5 rounded"
          style={{ left: `${((value - min) / (max - min)) * 100}%` }}
        >
          {value}
        </div>
      </div>
      <div className="flex justify-between text-xs text-gray-500 mt-1">
        <span>{min}</span>
        <span>{max}</span>
      </div>
      {error && <ErrorMessage message={error} />}
    </div>
  );
}
const gridOverlayStyle = {
  background:
    "linear-gradient(245.74deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0) 45.88%)",
  backgroundImage: "url('/assets/fire-calculator/overlay.png')", // Add the image URL here
  backgroundSize: "cover",
};

export default function FireCalculator() {
  const user = JSON.parse(getLocalStorage("userData") || "{}");
  const fireNumber = parseInt(getLocalStorage("fire_number") || "0") ?? 0;

  const navigate = useNavigate();
  const [currentAge, setCurrentAge] = useState(() => {
    const dob = new Date(user?.dob);
    const ageDifMs = Date.now() - dob.getTime();
    const ageDate = new Date(ageDifMs);
    const calculatedAge = Math.abs(ageDate.getUTCFullYear() - 1970);
    return Math.max(calculatedAge, 10);
  });
  const [retirementAge, setRetirementAge] = useState(45);
  const [monthlySpend, setMonthlySpend] = useState<number>(0);
  const [inflation, setInflation] = useState(7);
  const [coastFireAge, setCoastFireAge] = useState(40);
  const [error, setError] = useState<string>("");
  const [calculationResults, setCalculationResults] =
    useState<FireCalculatorResponse | null>(null);

  // Debounced values
  const debouncedCurrentAge = useDebounce(currentAge.toString(), 500);
  const debouncedRetirementAge = useDebounce(retirementAge.toString(), 500);
  const debouncedMonthlySpend = useDebounce(monthlySpend.toString(), 500);
  const debouncedInflation = useDebounce(inflation.toString(), 500);
  const debouncedCoastFireAge = useDebounce(coastFireAge.toString(), 500);

  // Initialize the lazy query
  const [getToolDetails, { data: toolData, isLoading }] =
    useLazyGetToolDetailsQuery();
  const [updateTool] = useUpdateToolMutation();

  useEffect(() => {
    const fetchData = async () => {
      if (fireNumber !== 0) {
        getToolDetails();
      } else {
        await updateTool({
          type: "fire_calculator",
          payload: {
            monthly_expense: 0,
            current_age: 0,
            retirement_age: 0,
            inflation_rate: 0,
            coast_fire_age: 0,
          },
          is_save: false,
        }).unwrap();
      }
    };

    fetchData();
  }, [fireNumber, getToolDetails, updateTool]);

  // Set initial values from API data
  useEffect(() => {
    if (toolData?.data?.fire_calculator) {
      const {
        current_age,
        retirement_age,
        monthly_expense,
        inflation_rate,
        coast_fire_age,
      } = toolData.data.fire_calculator;

      setCurrentAge(current_age || currentAge); // Use API value or calculated default
      setRetirementAge(retirement_age || 45);
      setMonthlySpend(monthly_expense);
      setInflation(inflation_rate || 7);
      setCoastFireAge(coast_fire_age || 40);
    }
  }, [toolData]);

  // Validate Coast FIRE age constraint
  useEffect(() => {
    if (coastFireAge >= retirementAge) {
      setError("Coast fire age cannot be greater than Retirement age");
    } else {
      setError("");
    }
  }, [coastFireAge, retirementAge]);

  // Recalculate on debounced input change
  useEffect(() => {
    if (
      (!error &&
        toolData?.data?.fire_calculator?.fire &&
        toolData?.data?.fire_calculator?.fire !== 0) ||
      (calculationResults && calculationResults.fire !== 0)
    ) {
      handleCalculate(false);
    }
  }, [
    debouncedCurrentAge,
    debouncedRetirementAge,
    debouncedMonthlySpend,
    debouncedInflation,
    debouncedCoastFireAge,
  ]);

  const isPageViewTracked = React.useRef(false);

  useEffect(() => {
    if (isPageViewTracked.current) return;
    trackEvent(MixpanelEvent.PAGE_VIEW, { page_name: "fire_calculator" });
    isPageViewTracked.current = true;
  }, []);

  const handleCalculate = async (isSave: boolean = true) => {
    if (parseFloat(debouncedMonthlySpend) === 0) {
      setError("Monthly spend cannot be 0");
      return;
    }
    try {
      const result = await updateTool({
        type: "fire_calculator",
        payload: {
          monthly_expense: parseFloat(debouncedMonthlySpend),
          current_age: parseInt(debouncedCurrentAge),
          retirement_age: parseInt(debouncedRetirementAge),
          inflation_rate: parseFloat(debouncedInflation),
          coast_fire_age: parseInt(debouncedCoastFireAge),
        },
        is_save: isSave,
      }).unwrap();

      if (result.success) {
        setCalculationResults(result.data);
        if (isSave) {
          trackEvent(MixpanelEvent.BUTTON_PRESSED, {
            button_name: "fire_calculator_save_button_pressed",
            ...result?.data
          });
          toastSuccess({ message: "Saved your FIRE number" });
        }
      } else {
        setError(result.error.message || "Something went wrong");
      }
    } catch (err) {
      setError("Failed to calculate FIRE numbers");
      toastError({ message: err });
    }
  };

  if (isLoading) {
    return <div className="text-white">Loading...</div>;
  }

  return (
    <div
      className="md:h-[calc(100vh-76px)] h-screen relative md:overflow-hidden overflow-auto text-white" // Adjust the height to take the full viewport minus the navbar height
      style={gridOverlayStyle}
    >
      <img
        src="/assets/fire-calculator/overlay.png"
        alt="FIRE Icon"
        className="absolute inset-0 z-[1]"
      />
      <div className="relative bg-black lg:px-32 lg:pt-10 lg:h-full">
        <div
          className="absolute cursor-pointer rotate-180 flex justify-end z-[2]"
          onClick={() => navigate("/home")}
        >
          <CaretRight />
        </div>
        <div className="flex flex-col items-center justify-center overflow-hidden">
          <h1 className="text-[#E6D5B8] text-5xl font-bold mb-2 italic bg-gradient-text text-transparent bg-clip-text">
            FIRE Calculator
          </h1>
          <p className="text-gray-300 mb-12">
            Calculate your path to financial freedom
          </p>
        </div>

        <div className="absolute max-w-6xl mx-auto z-[2]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Calculator Form */}
            <div className="bg-[#1C1C1C] md:rounded-lg p-8 h-full md:max-h-[calc(100vh-260px)] md:overflow-y-auto">
              <h2 className="text-2xl font-bold mb-8">
                Calculate Your FIRE Number
              </h2>

              <Slider
                label="What is your current age?"
                description="This will help us determine the number of years you have left to save."
                value={currentAge}
                onChange={setCurrentAge}
                min={10}
                max={100}
                step={1}
              />

              <Slider
                label="At what age do you plan to retire?"
                description="The age you stop earning actively & depend on your retirement corpus for your lifestyle until the end of your life expectancy."
                value={retirementAge}
                onChange={setRetirementAge}
                min={10}
                max={120}
                step={1}
              />

              <div className="mb-8">
                <label className="text-white text-base block mb-2">
                  How much do you currently spend each month?
                </label>
                <p className="text-gray-400 text-sm mb-2">
                  Includes expenses such as rent, groceries, entertainment and
                  other lifestyle costs to estimate your retirement corpus.
                </p>
                <div className="relative">
                  <input
                    type="text"
                    value={monthlySpend ? monthlySpend.toLocaleString() : ""}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      const value = e.target.value.replace(/,/g, "");
                      setMonthlySpend(value ? parseFloat(value) : 0);
                    }}
                    className="w-[90%] bg-[#404040] border border-[#808080] rounded-tl-[9px] rounded-bl-[9px] rounded-tr-none rounded-br-none px-4 py-2 text-white pr-8 focus:none focus:none hover:none hover:none underline:none focus:ring-0 focus:outline-none"
                    placeholder="0"
                    min={1000}
                    max={1000000000}
                    required
                  />
                  <span className="absolute rounded-tr-[9px] rounded-br-[9px] text-[#FFFFFF] bg-[#808080] px-4 py-[9px]">
                    ₹
                  </span>
                </div>
              </div>

              <Slider
                label="Assumed Inflation"
                description="The rate at which costs rise over time. The higher the inflation, the more you would need to save."
                value={inflation}
                onChange={setInflation}
                min={2}
                max={15}
                step={0.5}
                suffix="%"
              />

              <Slider
                label="Desired Coast FIRE Age"
                description="The age when your investments can grow independently to fund retirement, letting you work only to cover living expenses."
                value={coastFireAge}
                onChange={setCoastFireAge}
                min={10}
                max={100}
                step={1}
                error={error}
              />
              <Button
                customClasses="w-full rounded-xl"
                disabled={!!error}
                onClick={() => handleCalculate(true)}
                isSubmitting={isLoading}
              >
                {calculationResults ||
                (toolData?.data?.fire_calculator?.fire &&
                  toolData?.data?.fire_calculator?.fire !== 0)
                  ? "Save"
                  : "Calculate"}
              </Button>
            </div>

            {/* Right Column - Info Card */}
            {calculationResults ||
            (toolData?.data?.fire_calculator?.fire &&
              toolData?.data?.fire_calculator?.fire !== 0) ? (
              <div className="bg-cover bg-center md:rounded-lg overflow-hidden relative sticky top-0 z-[20]">
                <FireResults
                  fireNumber={
                    calculationResults?.fire ||
                    toolData?.data?.fire_calculator?.fire ||
                    0
                  }
                  leanFire={
                    calculationResults?.lean_fire ||
                    toolData?.data?.fire_calculator?.lean_fire ||
                    0
                  }
                  fatFire={
                    calculationResults?.fat_fire ||
                    toolData?.data?.fire_calculator?.fat_fire ||
                    0
                  }
                  coastFire={
                    calculationResults?.coast_fire ||
                    toolData?.data?.fire_calculator?.coast_fire ||
                    0
                  }
                />
              </div>
            ) : (
              <div
                className="bg-cover bg-center rounded-lg overflow-hidden relative max-h-[573px] z-[20]"
                style={{
                  backgroundImage: "url('assets/fire-calculator/fire.png')",
                }}
              >
                <div className="absolute inset-0 bg-black bg-opacity-0"></div>
                <div className="relative z-10 p-8">
                  <div className="bg-white bg-opacity-90 rounded-lg p-6 mb-8">
                    <h3 className="text-black text-xl mb-4">
                      <span className="font-bold">F</span>
                      inancial <span className="font-bold">I</span>
                      ndependence, <span className="font-bold">R</span>
                      etire <span className="font-bold">E</span>
                      arly
                    </h3>
                    <p className="text-gray-700 text-sm">
                      F.I.R.E is a movement that helps you achieve financial
                      freedom by saving aggressively, investing wisely, and
                      minimising expenses. This calculator decodes your path to
                      financial freedom, projecting how much you need to retire
                      based on your current and projected expenses with assumed
                      inflation and historical investment growth rates.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
